import React, { useEffect, useState } from "react";
import InboxView from "../../components/InboxView";
import { circlesApi } from "./circlesApi";
import { Modal, inr, shortDate, useBusy, useCirclesList } from "./circlesShared";
import "../../assets/css/PageHeading.css";

const todayIso = () => new Date().toISOString().slice(0, 10);

function PayoutModal({ agent, tdsDefault, onClose, onDone }) {
  const [commissions, setCommissions] = useState([]);
  const [picked, setPicked] = useState([]);
  const [form, setForm] = useState({ method: "UPI", reference: "", paid_on: todayIso(), tds_pct: String(tdsDefault ?? 2) });
  const { busy, error, setError, run } = useBusy();

  useEffect(() => {
    circlesApi
      .commissions({ status: "Approved", agent_id: agent.Agent_Id })
      .then((res) => {
        setCommissions(res.data || []);
        setPicked((res.data || []).map((c) => c.Commission_Id));
      })
      .catch((err) => setError(err.message));
  }, [agent.Agent_Id, setError]);

  const gross = commissions.filter((c) => picked.includes(c.Commission_Id)).reduce((t, c) => t + Number(c.Amount || 0), 0);
  const tds = Math.round(gross * Number(form.tds_pct || 0)) / 100;

  const submit = () =>
    run(async () => {
      await circlesApi.recordPayout({ agent_id: agent.Agent_Id, commission_ids: picked, ...form, tds_pct: Number(form.tds_pct) });
      onDone();
    });

  return (
    <Modal
      title={`Record payout to ${agent.Agent_Name}`}
      onClose={onClose}
      busy={busy}
      onSubmit={submit}
      footer={
        <>
          <button type="button" className="wl-btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="wl-btn wl-btn--primary" disabled={busy || picked.length === 0}>
            Record {inr(gross - tds)} paid
          </button>
        </>
      }
    >
      <p className="cx-hint">Pay the agent by UPI or bank transfer first, then record it here with the reference.</p>
      <div className="d-flex flex-column gap-1">
        {commissions.map((c) => (
          <label key={c.Commission_Id} className="cx-check">
            <input
              type="checkbox"
              checked={picked.includes(c.Commission_Id)}
              onChange={(e) => setPicked(e.target.checked ? [...picked, c.Commission_Id] : picked.filter((id) => id !== c.Commission_Id))}
            />
            <span>
              {inr(c.Amount)} · {c.Customer} · {c.Project_Name}
            </span>
          </label>
        ))}
      </div>
      <div className="cx-row">
        <label className="wl-field">
          <span>Method</span>
          <select value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })}>
            <option>UPI</option>
            <option>Bank</option>
          </select>
        </label>
        <label className="wl-field">
          <span>Paid on</span>
          <input type="date" value={form.paid_on} onChange={(e) => setForm({ ...form, paid_on: e.target.value })} />
        </label>
        <label className="wl-field">
          <span>TDS %</span>
          <input type="number" min="0" max="30" step="0.5" value={form.tds_pct} onChange={(e) => setForm({ ...form, tds_pct: e.target.value })} />
        </label>
      </div>
      <label className="wl-field">
        <span>UTR or bank reference</span>
        <input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder="UTR 431209876543" />
      </label>
      <p className="wl-muted">
        Gross {inr(gross)} · TDS {inr(tds)} · Net {inr(gross - tds)}
      </p>
      {error ? <div className="wl-alert wl-alert--error">{error}</div> : null}
    </Modal>
  );
}

export default function CirclesPayouts() {
  const [paying, setPaying] = useState(null);
  const [notice, setNotice] = useState(null);
  const { rows, extra, loading, error, reload } = useCirclesList(() => circlesApi.payouts(), []);
  const due = extra.due || [];

  return (
    <div className="p-2 p-md-3">
      <div className="co-page-head">
        <h2 className="co-page-head__title">Circles Payouts</h2>
      </div>
      {due.length > 0 ? (
        <div className="cx-due">
          {due.map((d) => (
            <div key={d.Agent_Id} className="cx-card">
              <h3>{d.Agent_Name}</h3>
              <div className="wl-name">{inr(d.Amount)} to pay</div>
              <div className="wl-sub mb-2">{d.Commissions} approved commission{d.Commissions === 1 ? "" : "s"}</div>
              <button type="button" className="wl-btn wl-btn--primary wl-btn--sm" onClick={() => setPaying(d)}>
                Record payout
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p className="cx-hint mb-2">Nothing to pay: approved commissions show here by agent.</p>
      )}
      <InboxView
        onRefresh={reload}
        notice={notice}
        onDismissNotice={() => setNotice(null)}
        error={error}
        loading={loading}
        rows={rows}
        rowKey={(p) => p.Payout_Id}
        empty="No payouts recorded yet."
        columns={[
          { key: "paid", label: "Paid on", render: (p) => shortDate(p.Paid_On) },
          { key: "agent", label: "Agent", render: (p) => p.Agent_Name },
          { key: "gross", label: "Gross", render: (p) => inr(p.Gross_Amount) },
          { key: "tds", label: "TDS", render: (p) => `${inr(p.TDS_Amount)} (${p.TDS_Pct}%)` },
          { key: "net", label: "Net", render: (p) => <strong>{inr(p.Net_Amount)}</strong> },
          { key: "method", label: "Method", render: (p) => p.Method },
          { key: "ref", label: "Reference", render: (p) => <span className="cx-mono">{p.Reference}</span> },
        ]}
        mobileCard={(p) => ({
          title: `${inr(p.Net_Amount)} · ${p.Agent_Name}`,
          fields: [
            { label: "Paid on", value: shortDate(p.Paid_On) },
            { label: "Gross · TDS", value: `${inr(p.Gross_Amount)} · ${inr(p.TDS_Amount)}` },
            { label: "Reference", value: `${p.Method} ${p.Reference}` },
          ],
        })}
      />
      {paying ? (
        <PayoutModal
          agent={paying}
          tdsDefault={extra.settings?.defaultTdsPct}
          onClose={() => setPaying(null)}
          onDone={() => {
            setNotice({ type: "success", text: `Payout to ${paying.Agent_Name} recorded.` });
            setPaying(null);
            reload();
          }}
        />
      ) : null}
    </div>
  );
}
